/**
 * 청구서 열람 — 로그인 없이, 사업자등록번호로
 * ---------------------------------------------------------------------------
 * ## 누가 여는 화면인가
 * 청구서 안내 메일을 받는 사람은 보통 **경리 담당자**다. Hub 계정이 없는
 * 경우가 많아, 로그인해야 내역을 볼 수 있으면 결국 전화가 온다.
 *
 * 그렇다고 링크만 알면 열리게 두면, 메일이 전달되거나 주소가 새는 순간
 * 남의 거래 금액이 보인다. 그래서 **링크(난수)와 사업자등록번호 둘 다**
 * 맞아야 열린다. 번호를 맞혀 보는 시도는 서버가 횟수로 막는다.
 *
 * ## 인쇄를 전제로 그린다
 * 받는 쪽은 이 화면을 출력해 결재에 붙인다. 화면용 장식을 줄이고, 금액이
 * 세로로 맞도록 숫자 칸을 오른쪽에 둔다. 인쇄할 때는 입력 칸과 단추를
 * 지운다 — 종이에 남으면 무슨 뜻인지 알 수 없는 얼룩이 된다.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Title, Text, Paragraph } = Typography;

interface InvoiceItem {
  item_type: string;
  product_code: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

interface PublicInvoice {
  invoice_no: string;
  status: string;
  period_start: string;
  period_end: string;
  issued_at: string | null;
  due_at: string | null;
  subtotal_amount: number;
  discount_amount: number;
  vat_amount: number;
  credit_amount: number;
  total_amount: number;
  paid_amount: number;
  tax_invoice_no: string | null;
  tax_invoice_at: string | null;
  company_name: string;
  biz_reg_no: string | null;
  zip_code: string | null;
  address1: string | null;
  address2: string | null;
  items: InvoiceItem[];
  payments: Array<{
    method: string | null;
    payer_name: string | null;
    amount: number;
    paid_at: string | null;
  }>;
  issuer: {
    companyName: string;
    bizRegNo: string;
    address: string;
    tel: string;
    email: string;
  };
}

const won = (v: number | null | undefined) =>
  `${Math.round(Number(v ?? 0)).toLocaleString('ko-KR')}원`;
const ymd = (v: string | null) => (v ? v.slice(0, 10) : '-');
const PAY_METHOD: Record<string, string> = {
  TRANSFER: '계좌이체',
  CARD: '카드',
  MANUAL: '수기 기록',
};
const STATUS: Record<string, { text: string; color: string }> = {
  ISSUED: { text: '발행', color: 'blue' },
  PARTIALLY_PAID: { text: '부분 납부', color: 'gold' },
  PAID: { text: '납부 완료', color: 'green' },
  OVERDUE: { text: '납기 경과', color: 'red' },
  VOID: { text: '무효', color: 'default' },
};

export function PublicInvoicePage() {
  const { token } = useParams<{ token: string }>();
  const [bizRegNo, setBizRegNo] = useState('');
  const [data, setData] = useState<PublicInvoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const open = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.post(`/public/invoices/${token}/open`, { bizRegNo });
      setData(res.data as PublicInvoice);
    } catch (e) {
      setError(errorText(e as NormalizedError));
    } finally {
      setLoading(false);
    }
  };

  /* ---- 아직 안 열렸다 ---- */
  if (!data) {
    return (
      <div style={{ maxWidth: 480, margin: '12vh auto', padding: 16 }}>
        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Title level={4} style={{ marginBottom: 0 }}>
              청구서 확인
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              이 청구서를 받으신 <b>기업의 사업자등록번호</b>를 입력해 주세요.
              <br />
              로그인하지 않으셔도 됩니다.
            </Paragraph>

            {error && <Alert type="error" showIcon message={error} />}

            <Form layout="vertical" onFinish={() => void open()}>
              <Form.Item label="사업자등록번호" style={{ marginBottom: 12 }}>
                <Input
                  size="large"
                  autoFocus
                  inputMode="numeric"
                  placeholder="000-00-00000"
                  value={bizRegNo}
                  maxLength={12}
                  onChange={(e) => setBizRegNo(e.target.value)}
                />
              </Form.Item>
              <Button
                type="primary"
                size="large"
                block
                htmlType="submit"
                loading={loading}
                disabled={bizRegNo.replace(/\D/g, '').length !== 10}
              >
                청구서 보기
              </Button>
            </Form>

            <Text type="secondary" style={{ fontSize: 12 }}>
              「-」는 넣으셔도 되고 안 넣으셔도 됩니다. 여러 번 틀리면 잠시 잠깁니다.
            </Text>
          </Space>
        </Card>
      </div>
    );
  }

  /* ---- 열렸다 ---- */
  const st = STATUS[data.status] ?? { text: data.status, color: 'default' };
  const unpaid = Math.max(0, data.total_amount - data.paid_amount);

  return (
    <div className="invoice-sheet" style={{ maxWidth: 820, margin: '24px auto', padding: 16 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={3} style={{ marginBottom: 0 }}>청구서</Title>
          <Space className="no-print">
            <Tag color={st.color}>{st.text}</Tag>
            <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
              인쇄
            </Button>
          </Space>
        </Space>

        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="청구서 번호">{data.invoice_no}</Descriptions.Item>
          <Descriptions.Item label="발행일">{ymd(data.issued_at)}</Descriptions.Item>
          <Descriptions.Item label="이용 기간">
            {ymd(data.period_start)} ~ {ymd(data.period_end)}
          </Descriptions.Item>
          <Descriptions.Item label="납기일">{ymd(data.due_at)}</Descriptions.Item>
          <Descriptions.Item label="공급받는자" span={2}>
            {data.company_name}
            {data.biz_reg_no ? ` (${data.biz_reg_no})` : ''}
            {data.address1 ? ` · ${data.address1} ${data.address2 ?? ''}`.trimEnd() : ''}
          </Descriptions.Item>
          <Descriptions.Item label="공급자" span={2}>
            {data.issuer.companyName} ({data.issuer.bizRegNo}) · {data.issuer.address}
            {data.issuer.tel ? ` · ${data.issuer.tel}` : ''}
          </Descriptions.Item>
          {data.tax_invoice_no && (
            <Descriptions.Item label="세금계산서" span={2}>
              {data.tax_invoice_no}
              {data.tax_invoice_at ? ` · ${ymd(data.tax_invoice_at)} 발급` : ''}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Table<InvoiceItem>
          rowKey={(r, i) => `${r.item_type}-${i}`}
          size="small"
          pagination={false}
          dataSource={data.items}
          columns={[
            { title: '항목', dataIndex: 'description', key: 'd' },
            {
              title: '수량',
              dataIndex: 'quantity',
              key: 'q',
              width: 80,
              align: 'right',
              render: (v: number) => v.toLocaleString('ko-KR'),
            },
            {
              title: '단가',
              dataIndex: 'unit_price',
              key: 'u',
              width: 120,
              align: 'right',
              render: (v: number) => won(v),
            },
            {
              title: '금액',
              dataIndex: 'amount',
              key: 'a',
              width: 130,
              align: 'right',
              render: (v: number) => (
                <Text type={v < 0 ? 'success' : undefined}>{won(v)}</Text>
              ),
            },
          ]}
        />

        <Descriptions bordered size="small" column={1} style={{ maxWidth: 420, marginLeft: 'auto' }}>
          <Descriptions.Item label="공급가액">
            {won(data.subtotal_amount - data.discount_amount)}
          </Descriptions.Item>
          <Descriptions.Item label="부가세">{won(data.vat_amount)}</Descriptions.Item>
          {data.credit_amount > 0 && (
            <Descriptions.Item label="환불 차감">
              <Text type="success">−{won(data.credit_amount)}</Text>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="청구 금액">
            <Text strong style={{ fontSize: 16 }}>{won(data.total_amount)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="납부액">{won(data.paid_amount)}</Descriptions.Item>
          <Descriptions.Item label="남은 금액">
            <Text strong type={unpaid > 0 ? 'danger' : 'success'}>{won(unpaid)}</Text>
          </Descriptions.Item>
        </Descriptions>

        {data.payments.length > 0 && (
          <Card size="small" title="납부 내역">
            <Table
              rowKey={(_, i) => String(i)}
              size="small"
              pagination={false}
              dataSource={data.payments}
              columns={[
                {
                  title: '납부일',
                  key: 'at',
                  width: 120,
                  render: (_: unknown, r: PublicInvoice['payments'][number]) => ymd(r.paid_at),
                },
                {
                  title: '수단',
                  key: 'm',
                  width: 110,
                  render: (_: unknown, r: PublicInvoice['payments'][number]) =>
                    PAY_METHOD[r.method ?? ''] ?? r.method ?? '-',
                },
                { title: '입금자명', dataIndex: 'payer_name', key: 'p' },
                {
                  title: '금액',
                  key: 'a',
                  width: 130,
                  align: 'right',
                  render: (_: unknown, r: PublicInvoice['payments'][number]) => won(r.amount),
                },
              ]}
            />
          </Card>
        )}

        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          문의 : {data.issuer.email}
          {data.issuer.tel ? ` · ${data.issuer.tel}` : ''}
        </Paragraph>
      </Space>

      {/* 인쇄할 때는 화면용 단추를 지운다 */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .invoice-sheet { margin: 0; max-width: none; }
        }
      `}</style>
    </div>
  );
}
